import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { fmtINR, fmtDate, daysLabel, bucketBadge, bucketLabel, STATUS_COLORS } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Zap, ChevronLeft, ChevronRight, Ban, ChevronDown } from "lucide-react";

// Prepaid-broadband upload-discipline badges (set by the /circuits endpoint).
const UPLOAD_FLAG_META = {
  due_soon_not_uploaded: { label: "Due soon · no receipt", cls: "bg-amber-100 text-amber-800" },
  overdue_not_uploaded: { label: "Overdue · no receipt", cls: "bg-orange-100 text-orange-800" },
  never_uploaded: { label: "Never uploaded", cls: "bg-rose-100 text-rose-700" },
  recharge_no_receipt: { label: "Receipt missing", cls: "bg-slate-200 text-slate-700" },
};

export default function CircuitTable({ baseFilters = {}, onRecharge, onCancel, selectable = false, selected = [], setSelected, refreshKey = 0, compact = false }) {
  const { can } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: compact ? 8 : 25 });
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("urgency");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ ...baseFilters, page, limit: compact ? 8 : 25, sort });
      Object.keys(baseFilters).forEach((k) => { if (!baseFilters[k]) params.delete(k); });
      const { data } = await api.get(`/circuits?${params.toString()}`);
      setData(data);
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [JSON.stringify(baseFilters), page, sort, refreshKey, compact]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [JSON.stringify(baseFilters)]);

  const pages = Math.max(1, Math.ceil(data.total / data.limit));
  const toggle = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const allOnPage = data.items.every((c) => selected.includes(c.id)) && data.items.length > 0;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm dense" data-testid="circuit-table">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
              {selectable && (
                <th className="px-3 py-3">
                  <input type="checkbox" checked={allOnPage} data-testid="select-all-rows"
                    onChange={() => allOnPage ? setSelected(selected.filter((id) => !data.items.some((c) => c.id === id))) : setSelected([...new Set([...selected, ...data.items.map((c) => c.id)])])} />
                </th>
              )}
              <th className="px-3 py-3">Priority</th>
              <th className="px-3 py-3">Customer</th>
              <th className="px-3 py-3">Vendor</th>
              <th className="px-3 py-3">Type</th>
              <th className="px-3 py-3">Due date</th>
              <th className="px-3 py-3">Days</th>
              <th className="px-3 py-3 text-right">Amount</th>
              <th className="px-3 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((c) => {
              const cols = (selectable ? 1 : 0) + 8;
              return [
              <tr key={c.id} className="row-enter border-b border-slate-100 hover:bg-slate-50/70" data-testid={`circuit-row-${c.id}`}>
                {selectable && (
                  <td className="px-3 py-2.5"><input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} data-testid={`row-check-${c.id}`} /></td>
                )}
                <td className="px-3 py-2.5">
                  {c.bucket ? <span className={`text-[10px] font-bold px-2 py-1 rounded-md whitespace-nowrap ${bucketBadge(c.bucket)}`}>{bucketLabel(c.bucket, c.days_remaining)}</span> : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2.5 cursor-pointer" onClick={() => nav(`/circuit/${c.id}`)}>
                  <div className="font-semibold text-slate-800 hover:text-blue-700">{c.customer_name}</div>
                  <div className="text-xs text-slate-400">{c.city || c.location_name || c.connection_name || "—"}</div>
                  {c.upload_flag && UPLOAD_FLAG_META[c.upload_flag] && (
                    <span className={`inline-block mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${UPLOAD_FLAG_META[c.upload_flag].cls}`}>
                      {UPLOAD_FLAG_META[c.upload_flag].label}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-slate-700">{c.vendor_name || <span className="text-red-500">missing</span>}</td>
                <td className="px-3 py-2.5"><span className={`text-xs px-2 py-0.5 rounded ${c.service_type === "ILL" ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}`}>{c.service_type}</span></td>
                <td className="px-3 py-2.5 font-mono-num text-xs whitespace-nowrap">{fmtDate(c.next_action_date)}</td>
                <td className="px-3 py-2.5">
                  <span className={`font-mono-num text-xs font-semibold whitespace-nowrap ${c.days_remaining < 0 ? "text-red-600" : c.days_remaining === 0 ? "text-rose-600" : c.days_remaining <= 3 ? "text-orange-600" : "text-slate-600"}`}>{daysLabel(c.days_remaining)}</span>
                </td>
                <td className="px-3 py-2.5 text-right font-mono-num font-semibold">{fmtINR(c.amount)}</td>
                <td className="px-3 py-2.5 text-right">
                  <div className="flex justify-end items-center gap-1">
                    {onRecharge && can("recharge") && ["Active"].includes(c.status) && (
                      <button data-testid={`recharge-btn-${c.id}`} onClick={() => onRecharge(c)}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-[var(--brand)] text-white hover:bg-[var(--brand-hover)]">
                        <Zap size={13} /> {c.billing_type === "Postpaid" ? "Pay" : "Recharge"}
                      </button>
                    )}
                    {onCancel && can("cancel") && c.status === "Active" && (
                      <button data-testid={`cancel-btn-${c.id}`} onClick={() => onCancel(c)} title="Customer cancellation"
                        className="inline-flex items-center text-xs px-2 py-1.5 rounded-lg border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600">
                        <Ban size={13} />
                      </button>
                    )}
                    <button onClick={() => toggleExpand(c.id)} data-testid={`expand-${c.id}`} title="Details"
                      className={`p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:text-slate-600 transition-transform ${expanded[c.id] ? "rotate-180" : ""}`}>
                      <ChevronDown size={14} />
                    </button>
                  </div>
                </td>
              </tr>,
              expanded[c.id] && (
                <tr key={c.id + "-x"} className="bg-slate-50/60 border-b border-slate-100">
                  <td colSpan={cols} className="px-5 py-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-xs">
                      <Detail label="Billable item" value={c.internal_circuit_id} mono />
                      <Detail label="Vendor account ID" value={c.isp_account_number || "— missing —"} mono danger={!c.isp_account_number} />
                      <Detail label="Vendor circuit ID" value={c.isp_circuit_id} mono />
                      <Detail label="Plan / bandwidth" value={c.plan_name || c.bandwidth} />
                      <Detail label="Site / location" value={c.location_name || c.city} />
                      <Detail label="Delivery" value={c.delivery_type} />
                      <Detail label="Assigned" value={c.assigned_accounts_name || "Unassigned"} danger={!c.assigned_accounts_name} />
                      <Detail label="Status" value={c.verification_pending ? "Verify Pending" : c.status} />
                    </div>
                    <button onClick={() => nav(`/circuit/${c.id}`)} className="mt-2 text-xs font-semibold text-blue-700 hover:underline">Open full detail →</button>
                  </td>
                </tr>
              ),
              ];
            })}
            {data.items.length === 0 && !loading && (
              <tr><td colSpan={(selectable ? 1 : 0) + 8} className="px-4 py-10 text-center text-slate-400">No billable items match this view.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 text-sm">
        <div className="text-slate-500">
          {data.total} record{data.total !== 1 ? "s" : ""} {selectable && selected.length > 0 && <span className="ml-2 text-blue-700 font-medium">· {selected.length} selected</span>}
        </div>
        <div className="flex items-center gap-2">
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-8 text-xs border border-slate-200 rounded-lg px-2" data-testid="sort-select">
            <option value="urgency">Sort: Urgency</option>
            <option value="amount">Sort: Amount</option>
            <option value="customer">Sort: Customer</option>
          </select>
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1.5 rounded-lg border border-slate-200 disabled:opacity-40" data-testid="prev-page"><ChevronLeft size={16} /></button>
          <span className="text-xs text-slate-500">Page {page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="p-1.5 rounded-lg border border-slate-200 disabled:opacity-40" data-testid="next-page"><ChevronRight size={16} /></button>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value, mono, danger }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`${mono ? "font-mono-num" : ""} ${danger ? "text-red-600" : "text-slate-700"}`}>{value || "—"}</div>
    </div>
  );
}
